import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import axiosInstance, {
  clearAuthData,
  isJwtAuthFailure,
} from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import ListPagination from "../../components/custom/ListPagination";
import useServerPaginatedList from "../../hooks/useServerPaginatedList";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast.js";
import { openAdminPanelAsUser } from "../../utils/adminHandoff";
import { formatDisplayDateTime as formatDateTime } from "../../utils/formatDisplayDate";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

const COMPANY_FILTERS = [
  { id: "all", label: "All companies", shortLabel: "All", status: "" },
  { id: "active", label: "Active", shortLabel: "Active", status: "active" },
  { id: "inactive", label: "Inactive", shortLabel: "Inactive", status: "inactive" },
  { id: "converted", label: "Converted", shortLabel: "Converted", status: "converted" },
  { id: "deleted", label: "Deleted companies", shortLabel: "Deleted", status: "deleted" },
];

const FILTER_STYLE = {
  all: { icon: "fa-building", color: "#495057", bg: "#f8f9fa" },
  active: { icon: "fa-check-circle", color: "#198754", bg: "#d1e7dd" },
  inactive: { icon: "fa-user-clock", color: "#d97706", bg: "#fef3c7" },
  converted: { icon: "fa-exchange", color: "#6c757d", bg: "#e9ecef" },
  deleted: { icon: "fa-trash", color: "#6c757d", bg: "#e9ecef" },
};

const cardRadius = { borderRadius: "14px" };

function buildCompaniesPath(status) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  const qs = params.toString();
  return `/companies${qs ? `?${qs}` : ""}`;
}

function getActiveFilterId(status) {
  const match = COMPANY_FILTERS.find((f) => f.status === status);
  return match ? match.id : "all";
}

function CompanyFilterPill({ filter, isActive, onSelect }) {
  const style = FILTER_STYLE[filter.id] || FILTER_STYLE.all;
  return (
    <button
      type="button"
      className="companies-filter-pill"
      aria-pressed={isActive}
      title={filter.label}
      onClick={() => onSelect(filter)}
      style={
        isActive
          ? {
              backgroundColor: style.bg,
              borderColor: style.color,
              color: style.color,
              boxShadow: `inset 0 0 0 1px ${style.color}`,
            }
          : undefined
      }
    >
      <i className={`fa ${style.icon} fa-fw me-1`} />
      {filter.shortLabel || filter.label}
    </button>
  );
}

const emptyForm = () => ({
  CompanyName: "",
  FullName: "",
  Email: "",
  Password: "",
  Phone: "",
  Address: "",
  City: "",
  State: "",
  PostalCode: "",
  EIN: "",
  Country: "US",
});

const Companies = () => {
  const location = useLocation();
  const history = useHistory();
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [convertingId, setConvertingId] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [managingId, setManagingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [loggingInId, setLoggingInId] = useState(null);

  const { status, activeFilterId, activeFilterLabel } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const nextStatus = (params.get("status") || "").trim().toLowerCase();
    const filterId = getActiveFilterId(nextStatus);
    const filter = COMPANY_FILTERS.find((f) => f.id === filterId) || COMPANY_FILTERS[0];
    return {
      status: filter.status,
      activeFilterId: filter.id,
      activeFilterLabel: filter.label,
    };
  }, [location.search]);

  const fetchCompaniesPage = useCallback(
    async ({ search, page, limit }) => {
      try {
        const params = { search, page, limit };
        if (status) params.status = status;
        return await axiosInstance.get("api/company/GetAll", { params });
      } catch (error) {
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          return { data: { data: [], total: 0 } };
        }
        showErrorToast(error.response?.data?.message || "Failed to load companies");
        throw error;
      }
    },
    [status]
  );

  const {
    rows,
    totalCount,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    isLoading,
    entriesPerPage,
    refetch,
  } = useServerPaginatedList(fetchCompaniesPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [status, setCurrentPage]);

  const applyFilter = (filter) => {
    history.push(buildCompaniesPath(filter.status));
  };

  const busy = !!(convertingId || assigningId || managingId || deletingId || loggingInId);
  const isConverted = (row) => !!(row.ConvertedAt || row.ConvertedToFisherID);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosInstance.post("api/company/Create", form);
      showSuccessToast("Company created");
      setShowCreate(false);
      setForm(emptyForm());
      await refetch();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    if (busy || isConverted(row)) return;
    try {
      await axiosInstance.post(`api/company/Update/${row.id}`, {
        Status: !row.Status,
      });
      showSuccessToast("Status updated");
      await refetch();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Update failed");
    }
  };

  const loginAsCompany = async (row) => {
    if (busy || !row.Status || isConverted(row)) return;
    setLoggingInId(row.id);
    try {
      const res = await axiosInstance.post("api/company/LoginAsCompany", {
        companyId: row.id,
      });
      openAdminPanelAsUser(res.data);
      showSuccessToast("Opening company AdminPanel sessionΓÇª");
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Login-as failed");
    } finally {
      setLoggingInId(null);
    }
  };

  const convertToPondOwner = async (row) => {
    if (busy || isConverted(row)) return;
    const result = await Swal.fire({
      title: `Convert ${row.Name}?`,
      html: `
        <p class="text-start small mb-2">This will detach all managed accounts, retire the company login, and create a pond owner with a <strong>14-day trial</strong> for the company owner email.</p>
        <p class="text-start small text-muted mb-0">Once only ΓÇö cannot be undone.</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Convert",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d9488",
      focusCancel: true,
    });
    if (!result.isConfirmed) return;

    setConvertingId(row.id);
    try {
      const res = await axiosInstance.post(
        `api/company/${row.id}/convert-to-pond-owner`,
        { confirm: true }
      );
      showSuccessToast(
        res.data?.message ||
          `Converted. New fisher id: ${res.data?.fisher?.id ?? "ΓÇö"}`
      );
      await refetch();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Conversion failed");
    } finally {
      setConvertingId(null);
    }
  };

  const assignAccount = async (row) => {
    if (busy || isConverted(row) || !row.Status) return;

    const emailResult = await Swal.fire({
      title: `Assign pond owner to ${row.Name}`,
      html: `<p class="text-start small mb-2">Enter an independent pond owner email. Package and end date stay the same; the company handles renewals afterward. No invitation ΓÇö links immediately.</p>`,
      input: "email",
      inputPlaceholder: "owner@example.com",
      showCancelButton: true,
      confirmButtonText: "Assign",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d9488",
      inputValidator: (value) => {
        if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())) {
          return "Enter a valid email";
        }
        return null;
      },
    });
    if (!emailResult.isConfirmed) return;
    const email = String(emailResult.value || "").trim();

    setAssigningId(row.id);
    try {
      const res = await axiosInstance.post(`api/company/${row.id}/attach-account`, {
        email,
      });
      showSuccessToast(res.data?.message || "Account assigned");
      await refetch();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Assign failed");
    } finally {
      setAssigningId(null);
    }
  };

  const manageAccounts = async (row) => {
    if (busy) return;
    setManagingId(row.id);
    try {
      const res = await axiosInstance.get(`api/company/${row.id}/managed-accounts`);
      const accounts = Array.isArray(res.data) ? res.data : [];
      if (accounts.length === 0) {
        await Swal.fire({
          title: row.Name,
          text: "No managed pond owner accounts yet.",
          icon: "info",
          confirmButtonColor: "#0d9488",
        });
        return;
      }

      const options = {};
      accounts.forEach((a) => {
        options[String(a.id)] = `${a.FullName} (${a.Email})`;
      });

      const pick = await Swal.fire({
        title: `Managed by ${row.Name}`,
        html: `<p class="text-start small mb-2">Select an account to remove from this consultancy. They keep ponds and package dates; company billing stops.</p>`,
        input: "select",
        inputOptions: options,
        inputPlaceholder: "Choose accountΓÇª",
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Remove from company",
        denyButtonText: "Close",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc3545",
        denyButtonColor: "#6c757d",
        focusCancel: true,
        inputValidator: (value) => {
          if (!value) return "Select an account";
          return null;
        },
      });

      if (!pick.isConfirmed || !pick.value) return;

      const account = accounts.find((a) => String(a.id) === String(pick.value));
      const confirm = await Swal.fire({
        title: "Remove from consultancy?",
        html: `<p class="mb-0">Remove <strong>${account?.FullName || "this account"}</strong> from <strong>${row.Name}</strong>?</p>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, remove",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#dc3545",
        focusCancel: true,
      });
      if (!confirm.isConfirmed) return;

      const detachRes = await axiosInstance.post(
        `api/fisher/DetachFromCompany?id=${pick.value}`
      );
      showSuccessToast(
        detachRes.data?.message || "Account removed from consultancy"
      );
      await refetch();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Could not load managed accounts");
    } finally {
      setManagingId(null);
    }
  };

  const deleteCompanyRow = async (row) => {
    if (busy || row.DeletedAt) return;

    const managedCount = Number(row.ManagedAccountCount) || 0;
    const result = await Swal.fire({
      title: `Delete ${row.Name}?`,
      html: `
        <p class="text-start small mb-2">Choose what to do:</p>
        <ul class="text-start small mb-2">
          <li><strong>Keep data</strong> ΓÇö remove company identity and manager logins; release managed accounts; keep Postal/ZIP on a Deleted company shell.</li>
          <li><strong>Delete everything</strong> ΓÇö permanently remove the company record (managed accounts are still released first).</li>
        </ul>
        ${
          managedCount > 0
            ? `<p class="text-start small mb-0"><strong>${managedCount}</strong> managed pond owner account${
                managedCount === 1 ? "" : "s"
              } will be released either way.</p>`
            : ""
        }
      `,
      icon: "warning",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Keep data, delete account",
      denyButtonText: "Delete everything",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d9488",
      denyButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      focusCancel: true,
    });
    if (result.isDismissed) return;

    const mode = result.isConfirmed ? "keep" : "wipe";

    setDeletingId(row.id);
    try {
      const res = await axiosInstance.post(`api/company/CloseAccount/${row.id}`, {
        confirm: true,
        mode,
      });
      showSuccessToast(
        res.data?.message ||
          (mode === "keep" ? "Company closed; data retained" : "Company permanently deleted")
      );
      await refetch();
    } catch (error) {
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return;
      }
      showErrorToast(error.response?.data?.message || "Failed to delete company");
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading && rows.length === 0 && deletingId == null) {
    return <Preloader />;
  }

  return (
    <div className="companies-page">
      <style>{`
        .companies-page {
          display: flex;
          flex-direction: column;
          min-height: calc(100vh - 6.5rem);
        }
        .companies-table-card {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          min-height: 28rem;
        }
        .companies-table-card > .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .companies-table-scroll {
          flex: 1 1 auto;
          min-height: 22rem;
          overflow: auto;
        }
        .companies-filter-toolbar {
          background: #fff;
          border: 1px solid #e9ecef;
          border-radius: 14px;
        }
        .companies-filter-pill {
          display: inline-flex;
          align-items: center;
          padding: 0.3rem 0.65rem;
          font-size: 0.75rem;
          font-weight: 500;
          line-height: 1;
          color: #495057;
          background: #fff;
          border: 1px solid #dee2e6;
          border-radius: 999px;
          transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .companies-filter-pill .fa {
          font-size: 0.7rem;
        }
        .companies-filter-pill:hover {
          background: #f8f9fa;
          border-color: #ced4da;
        }
        .companies-filter-pill:focus-visible {
          outline: 2px solid #86b7fe;
          outline-offset: 2px;
        }
        .companies-filter-divider {
          width: 1px;
          height: 1.25rem;
          background: #dee2e6;
          flex-shrink: 0;
        }
        .companies-search-input {
          border-radius: 8px !important;
          min-height: 34px;
          font-size: 0.875rem;
        }
        .companies-count-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 2rem;
          height: 2rem;
          padding: 0 0.65rem;
          border-radius: 999px;
          font-size: 0.875rem;
          font-weight: 600;
          line-height: 1;
          color: #374151;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
        }
        .companies-create-card {
          border-radius: 14px;
        }
      `}</style>

      <div className="d-flex align-items-center mb-3">
        <div>
          <ul className="breadcrumb mb-2">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Home</Link>
            </li>
            <li className="breadcrumb-item active">Companies</li>
          </ul>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
            <h1 className="page-header mb-0">Pond Manager Companies</h1>
            <span className="companies-count-badge" aria-label="Total companies">
              {isLoading ? "ΓÇª" : totalCount}
            </span>
            {!isLoading && activeFilterId !== "all" ? (
              <span
                className="small fw-medium"
                style={{ color: FILTER_STYLE[activeFilterId]?.color }}
              >
                {activeFilterLabel}
              </span>
            ) : null}
          </div>
          <p className="text-muted small mb-0">
            Free company accounts that manage and pay for pond owners
          </p>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn btn-success btn-rounded px-4 rounded-pill"
            onClick={() => setShowCreate((v) => !v)}
            aria-label="Create company"
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900" aria-hidden="true" />
            Create company
          </button>
        </div>
      </div>

      {showCreate ? (
        <form
          className="card border-0 shadow-sm p-3 mb-3 companies-create-card"
          onSubmit={handleCreate}
          style={{ maxWidth: 640 }}
        >
          <h5 className="mb-3">Create company (no charge)</h5>
          <div className="mb-2">
            <label className="form-label">Company name</label>
            <input
              className="form-control"
              required
              value={form.CompanyName}
              onChange={(e) => setForm({ ...form, CompanyName: e.target.value })}
            />
          </div>
          <div className="mb-2">
            <label className="form-label">Manager name</label>
            <input
              className="form-control"
              required
              value={form.FullName}
              onChange={(e) => setForm({ ...form, FullName: e.target.value })}
            />
          </div>
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                required
                value={form.Email}
                onChange={(e) => setForm({ ...form, Email: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                className="form-control"
                required
                value={form.Phone}
                onChange={(e) => setForm({ ...form, Phone: e.target.value })}
              />
            </div>
          </div>
          <div className="mb-2">
            <label className="form-label">Street address</label>
            <input
              className="form-control"
              required
              value={form.Address}
              onChange={(e) => setForm({ ...form, Address: e.target.value })}
            />
          </div>
          <div className="row g-2 mb-2">
            <div className="col-md-4">
              <label className="form-label">City</label>
              <input
                className="form-control"
                required
                value={form.City}
                onChange={(e) => setForm({ ...form, City: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">State / region</label>
              <input
                className="form-control"
                value={form.State}
                onChange={(e) => setForm({ ...form, State: e.target.value })}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Postal / ZIP</label>
              <input
                className="form-control"
                value={form.PostalCode}
                onChange={(e) => setForm({ ...form, PostalCode: e.target.value })}
              />
            </div>
          </div>
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <label className="form-label">Country (ISO)</label>
              <input
                className="form-control"
                maxLength={2}
                value={form.Country}
                onChange={(e) =>
                  setForm({ ...form, Country: e.target.value.toUpperCase().slice(0, 2) })
                }
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">EIN (optional)</label>
              <input
                className="form-control"
                value={form.EIN}
                onChange={(e) => setForm({ ...form, EIN: e.target.value })}
              />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Password</label>
            <input
              type="password"
              className="form-control"
              minLength={8}
              required
              value={form.Password}
              onChange={(e) => setForm({ ...form, Password: e.target.value })}
            />
          </div>
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "SavingΓÇª" : "Create"}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setShowCreate(false);
                setForm(emptyForm());
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      <div className="card border-0 shadow-sm mb-3 companies-filter-toolbar" style={cardRadius}>
        <div className="card-body py-2 px-3">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="d-flex flex-wrap align-items-center gap-1">
              {COMPANY_FILTERS.map((filter, index) => (
                <React.Fragment key={filter.id}>
                  {index === 1 ? (
                    <span className="companies-filter-divider" aria-hidden="true" />
                  ) : null}
                  <CompanyFilterPill
                    filter={filter}
                    isActive={activeFilterId === filter.id}
                    onSelect={applyFilter}
                  />
                </React.Fragment>
              ))}
            </div>

            <div
              className="position-relative flex-grow-1 ms-md-auto"
              style={{ minWidth: "160px", maxWidth: "280px" }}
            >
              <div
                className="position-absolute top-50 translate-middle-y text-muted ps-2"
                style={{ zIndex: 2, fontSize: "0.8rem" }}
              >
                <i className="fa fa-search opacity-50" />
              </div>
              <input
                type="search"
                className="form-control form-control-sm companies-search-input border bg-light ps-4"
                placeholder="Search name, email, EINΓÇª"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="d-flex align-items-center gap-2 ms-auto ms-md-0 text-nowrap">
              {activeFilterId !== "all" || searchTerm ? (
                <Link
                  to="/companies"
                  className="small text-decoration-none"
                  onClick={() => setSearchTerm("")}
                >
                  Clear
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm companies-table-card" style={cardRadius}>
        <div className="card-body p-0">
          <div className="table-responsive mb-0 position-relative companies-table-scroll">
            {busy ? (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                style={{ zIndex: 5 }}
              >
                <div className="text-muted">
                  <i className="fa fa-spinner fa-spin me-2" />
                  {deletingId != null
                    ? "Deleting companyΓÇª"
                    : assigningId != null
                      ? "Assigning accountΓÇª"
                      : convertingId != null
                        ? "ConvertingΓÇª"
                        : managingId != null
                          ? "Loading managed accountsΓÇª"
                          : loggingInId != null
                            ? "Opening sessionΓÇª"
                            : "WorkingΓÇª"}
                </div>
              </div>
            ) : null}
            <table className="table table-hover table-panel align-middle mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Location</th>
                  <th>Accounts</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      LoadingΓÇª
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      No companies match this filter.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="fw-medium">{row.Name}</div>
                        {row.EIN ? (
                          <div className="small text-muted">EIN {row.EIN}</div>
                        ) : null}
                        {row.OwnerEmail ? (
                          <div className="small text-muted">Manager: {row.OwnerEmail}</div>
                        ) : null}
                      </td>
                      <td>{row.Email}</td>
                      <td>{row.Phone || "ΓÇö"}</td>
                      <td className="small">
                        {[row.City, row.State, row.Country].filter(Boolean).join(", ") || "ΓÇö"}
                      </td>
                      <td>{row.ManagedAccountCount ?? 0}</td>
                      <td
                        onClick={() => {
                          if (!isConverted(row) && !row.DeletedAt) toggleStatus(row);
                        }}
                        style={
                          !isConverted(row) && !row.DeletedAt ? { cursor: "pointer" } : undefined
                        }
                      >
                        {row.DeletedAt ? (
                          <span className="badge border border-secondary text-secondary px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                            <i className="fa fa-circle fs-9px fa-fw me-5px" />
                            Deleted
                          </span>
                        ) : isConverted(row) ? (
                          <span className="badge border border-secondary text-secondary px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                            <i className="fa fa-circle fs-9px fa-fw me-5px" />
                            Converted
                          </span>
                        ) : row.Status ? (
                          <span className="badge border border-success text-success px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                            <i className="fa fa-circle fs-9px fa-fw me-5px" />
                            Active
                          </span>
                        ) : (
                          <span className="badge border border-danger text-warning px-2 pt-5px pb-5px rounded fs-12px d-inline-flex align-items-center">
                            <i className="fa fa-circle fs-9px fa-fw me-5px" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td>{formatDateTime(row.createdAt)}</td>
                      <td>
                        {row.DeletedAt ? (
                          <span className="small text-muted">Closed</span>
                        ) : (
                          <>
                        <button
                          type="button"
                          className="btn btn-link p-0 border-0 text-primary"
                          title="Login as company"
                          disabled={!row.Status || isConverted(row) || busy}
                          onClick={(event) => {
                            event.stopPropagation();
                            loginAsCompany(row);
                          }}
                        >
                          <i className="fa fa-sign-in fs-13px fa-fw me-5px" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link p-0 border-0"
                          title="Assign account"
                          style={{ color: "#0d9488" }}
                          disabled={!row.Status || isConverted(row) || busy}
                          onClick={(event) => {
                            event.stopPropagation();
                            assignAccount(row);
                          }}
                        >
                          <i className="fa fa-link fs-13px fa-fw me-5px" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link p-0 border-0 text-secondary"
                          title="Managed accounts"
                          disabled={busy}
                          onClick={(event) => {
                            event.stopPropagation();
                            manageAccounts(row);
                          }}
                        >
                          <i className="fa fa-users fs-13px fa-fw me-5px" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link p-0 border-0 text-success"
                          title="Convert to pond owner"
                          disabled={isConverted(row) || busy}
                          onClick={(event) => {
                            event.stopPropagation();
                            convertToPondOwner(row);
                          }}
                        >
                          <i className="fa fa-exchange fs-13px fa-fw me-5px" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link p-0 border-0 text-danger"
                          title="Delete company"
                          disabled={busy}
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteCompanyRow(row);
                          }}
                        >
                          <i className="fa fa-trash fs-13px fa-fw me-5px" />
                        </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <ListPagination
            currentPage={currentPage}
            totalCount={totalCount}
            entriesPerPage={entriesPerPage}
            onPageChange={setCurrentPage}
            className="px-3 pb-3"
          />
        </div>
      </div>
    </div>
  );
};

export default Companies;
